import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Crown, Loader2, User, Mail, Phone, Users } from 'lucide-react';
import { toast } from 'sonner';
import { adminUsersAPI, teamsV2API } from '@/lib/api';
import { invalidatePrefix } from '@/lib/dataCache';

/**
 * Création VP en 1 clic (Admin) : crée le VP + son équipe (VP = payeur).
 * Barèmes par défaut : Easy Power 30%, Chantier 15% — modifiables ici et plus tard
 * par l'Admin dans les paramètres / par dossier.
 */
export function CreateVpDialog({ open, onOpenChange, onCreated }) {
    const [loading, setLoading] = useState(false);
    const [teamNameTouched, setTeamNameTouched] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        commission_ep: 30,
        commission_ch: 15,
        team_name: '',
    });

    const defaultTeamName = (first) => (first.trim() ? `Équipe ${first.trim()}` : '');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => {
            const next = { ...prev, [name]: value };
            if (name === 'first_name' && !teamNameTouched) {
                next.team_name = defaultTeamName(value);
            }
            return next;
        });
    };

    const reset = () => {
        setFormData({ first_name: '', last_name: '', email: '', phone: '', commission_ep: 30, commission_ch: 15, team_name: '' });
        setTeamNameTouched(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.first_name.trim() || !formData.last_name.trim()) {
            toast.error('Nom et prénom sont obligatoires');
            return;
        }
        if (!formData.email.trim() || !formData.email.includes('@')) {
            toast.error('Email invalide');
            return;
        }
        const teamName = (formData.team_name.trim() || defaultTeamName(formData.first_name) || `Équipe ${formData.last_name.trim()}`);

        setLoading(true);
        try {
            const tempPassword = `Valnexo${Math.random().toString(36).substring(2, 8)}!`;
            const payload = {
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim(),
                email: formData.email.trim().toLowerCase(),
                phone: formData.phone.trim() || null,
                password: tempPassword,
                role: 'vp',
                commission_rates: {
                    easy_power: { pipeline_type: 'easy_power', commission_type: 'percentage', value: parseFloat(formData.commission_ep) || 30, is_active: true },
                    chantier: { pipeline_type: 'chantier', commission_type: 'percentage', value: parseFloat(formData.commission_ch) || 15, is_active: true },
                },
            };

            const res = await adminUsersAPI.create(payload);
            const newVpId = res.data?.user_id;

            // Rattachement automatique : création de son équipe (VP = payeur)
            let teamCreated = false;
            if (newVpId) {
                try {
                    await teamsV2API.create({ name: teamName, payer_vp_id: newVpId });
                    teamCreated = true;
                } catch (teamErr) {
                    console.error('Erreur création équipe VP:', teamErr);
                }
            }

            invalidatePrefix('dashboard');
            invalidatePrefix('pilotage');
            toast.success(
                <div>
                    <p className="font-semibold">VP créé avec succès !</p>
                    <p className="text-sm text-slate-600">
                        {teamCreated ? `Équipe « ${teamName} » créée. ` : ''}Un email de bienvenue a été envoyé à {formData.email}
                    </p>
                </div>
            );
            reset();
            onOpenChange(false);
            onCreated?.();
        } catch (error) {
            console.error('Erreur création VP:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg" data-testid="create-vp-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Crown className="w-5 h-5 text-amber-500" />
                        Créer un VP
                    </DialogTitle>
                    <DialogDescription>
                        Crée un VP et son équipe automatiquement. Un email avec ses identifiants lui sera envoyé.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label htmlFor="vp_first_name" className="flex items-center gap-2"><User className="w-4 h-4" />Prénom *</Label>
                            <Input id="vp_first_name" name="first_name" value={formData.first_name} onChange={handleChange}
                                placeholder="Pierre" required data-testid="vp-input-first-name" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="vp_last_name">Nom *</Label>
                            <Input id="vp_last_name" name="last_name" value={formData.last_name} onChange={handleChange}
                                placeholder="Martin" required data-testid="vp-input-last-name" />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="vp_email" className="flex items-center gap-2"><Mail className="w-4 h-4" />Email *</Label>
                        <Input id="vp_email" name="email" type="email" value={formData.email} onChange={handleChange}
                            placeholder="pierre.martin@valnexo.com" required data-testid="vp-input-email" />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="vp_phone" className="flex items-center gap-2"><Phone className="w-4 h-4" />Téléphone</Label>
                        <Input id="vp_phone" name="phone" value={formData.phone} onChange={handleChange}
                            placeholder="06 12 34 56 78" data-testid="vp-input-phone" />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="vp_team_name" className="flex items-center gap-2"><Users className="w-4 h-4" />Nom de l'équipe</Label>
                        <Input id="vp_team_name" name="team_name"
                            value={formData.team_name}
                            onChange={(e) => { setTeamNameTouched(true); handleChange(e); }}
                            placeholder="Équipe Pierre" data-testid="vp-input-team-name" />
                        <p className="text-xs text-slate-500">Créée automatiquement avec ce VP comme payeur.</p>
                    </div>

                    <div className="p-4 bg-amber-50 rounded-lg space-y-3">
                        <Label className="text-amber-800 font-medium">Barèmes de commission VP</Label>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label className="text-sm text-amber-700">Easy Power (%)</Label>
                                <Input type="number" name="commission_ep" value={formData.commission_ep} onChange={handleChange}
                                    min="0" max="100" className="bg-white" data-testid="vp-input-commission-ep" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-sm text-amber-700">Chantier (%)</Label>
                                <Input type="number" name="commission_ch" value={formData.commission_ch} onChange={handleChange}
                                    min="0" max="100" className="bg-white" data-testid="vp-input-commission-ch" />
                            </div>
                        </div>
                        <p className="text-xs text-amber-600">Modifiable plus tard par l'Admin dans les paramètres et par dossier.</p>
                    </div>

                    <DialogFooter className="mt-6">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                        <Button type="submit" disabled={loading} className="bg-amber-500 hover:bg-amber-600" data-testid="vp-submit-btn">
                            {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Création...</>) : (<><Crown className="w-4 h-4 mr-2" />Créer le VP</>)}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default CreateVpDialog;
